import { FormEvent, useState } from 'react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Textarea } from '../ui/textarea';
import SourceCombobox from '../common/SourceCombobox';
import { leadSources, assignees, leadStatuses } from '../../constants/leadData';
import {
  preventInvalidNameInput,
  preventInvalidNumberInput,
  preventInvalidNumberPaste,
  VALIDATION_MESSAGES,
} from '../../utils/validationMessages';

interface CreateLeadFormProps {
  onSubmit?: (data: LeadData) => void;
}
export interface LeadData {
  name: string;
  email: string;
  phone: string;
  source: string;
  status: 'New' | 'Contacted' | 'Qualified' | 'Proposal' | 'Converted' | 'Lost';
  assignedTo: string;
  value: number;
  notes: string;
  company: string;
  position: string;
}
const CreateLeadForm = ({ onSubmit }: CreateLeadFormProps) => {
  const [formData, setFormData] = useState<LeadData>({
    name: '',
    email: '',
    phone: '',
    source: 'Website',
    status: 'New',
    assignedTo: '',
    value: 0,
    notes: '',
    company: '',
    position: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof LeadData, string>>>(
    {}
  );

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof LeadData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = VALIDATION_MESSAGES.fullName.required;
    } else if (!/^[a-zA-Z \-']+$/.test(formData.name.trim())) {
      newErrors.name = VALIDATION_MESSAGES.fullName.pattern;
    } else if (formData.name.trim().length < 2) {
      newErrors.name = VALIDATION_MESSAGES.fullName.minLength;
    } else if (formData.name.trim().length > 50) {
      newErrors.name = VALIDATION_MESSAGES.fullName.maxLength;
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\s\-+()]+$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone format';
    }

    if (!formData.assignedTo.trim()) {
      newErrors.assignedTo = 'Assignment is required';
    }

    if (formData.value < 0) {
      newErrors.value = 'Value must be positive';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (validateForm() && onSubmit) {
      onSubmit(formData);
    }
  };

  const handleInputChange = (field: keyof LeadData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Personal Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                placeholder="Enter full name"
                value={formData.name}
                onKeyDown={preventInvalidNameInput}
                onChange={e => handleInputChange('name', e.target.value)}
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={e => handleInputChange('email', e.target.value)}
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                placeholder="Enter phone number"
                value={formData.phone}
                onChange={e => handleInputChange('phone', e.target.value)}
                className={errors.phone ? 'border-red-500' : ''}
              />
              {errors.phone && (
                <p className="text-sm text-red-500">{errors.phone}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="position">Position/Title</Label>
              <Input
                id="position"
                placeholder="Enter job position"
                value={formData.position}
                onChange={e => handleInputChange('position', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="company">Company</Label>
            <Input
              id="company"
              placeholder="Enter company name"
              value={formData.company}
              onChange={e => handleInputChange('company', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Lead Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Lead Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="source">Lead Source</Label>
              <SourceCombobox
                id="source"
                items={leadSources}
                placeholder="Select lead source"
                value={formData.source}
                onChange={value => handleInputChange('source', value)}
                emptyText="No lead sources found."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Lead Status</Label>
              <SourceCombobox
                id="status"
                items={leadStatuses}
                placeholder="Select lead status"
                value={formData.status}
                onChange={value =>
                  handleInputChange('status', value as LeadData['status'])
                }
                emptyText="No lead statuses found."
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              {/* 
                   TODO: Implement dynamic "Assigned To" functionality.
                   Currently, static data is being used.
                   In the future, assignees should be fetched dynamically based on users with the Sales role.
              */}
              <Label htmlFor="assignedTo">Assigned To *</Label>
              <SourceCombobox
                id="assignedTo"
                items={assignees}
                placeholder="Select assignee"
                value={formData.assignedTo}
                onChange={value => handleInputChange('assignedTo', value)}
                className={errors.assignedTo ? 'border-red-500' : ''}
                emptyText="No assignees found."
              />
              {errors.assignedTo && (
                <p className="text-sm text-red-500">{errors.assignedTo}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="value">Potential Value ($)</Label>
              <Input
                id="value"
                type="number"
                min="0"
                step="1000"
                placeholder="Enter potential value"
                value={formData.value || ''}
                onKeyDown={preventInvalidNumberInput}
                onPaste={preventInvalidNumberPaste}
                onChange={e =>
                  handleInputChange('value', parseInt(e.target.value) || 0)
                }
                className={errors.value ? 'border-red-500' : ''}
              />
              {errors.value && (
                <p className="text-sm text-red-500">{errors.value}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Enter any additional notes about this lead..."
              value={formData.notes}
              onChange={e => handleInputChange('notes', e.target.value)}
              rows={4}
            />
          </div>
        </CardContent>
      </Card>

      {/* Lead Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Lead Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Name:</span>
              <span className="text-sm font-medium">
                {formData.name || 'Not provided'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Email:</span>
              <span className="text-sm font-medium">
                {formData.email || 'Not provided'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Phone:</span>
              <span className="text-sm font-medium">
                {formData.phone || 'Not provided'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Source:</span>
              <span className="text-sm font-medium">{formData.source}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Status:</span>
              <span className="text-sm font-medium">{formData.status}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Assigned To:</span>
              <span className="text-sm font-medium">
                {formData.assignedTo || 'Not assigned'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Potential Value:</span>
              <span className="text-sm font-medium">
                {formData.value > 0
                  ? `$${formData.value.toLocaleString()}`
                  : 'Not specified'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default CreateLeadForm;
